/**
 * Legal & policy pages — one file, five exports.
 *
 * Each export is a thin wrapper around <LegalPage /> with the right
 * plain-English summary and section bodies. Final legalese is the
 * responsibility of counsel.
 *
 * Iteration 46 (sprint #3): Privacy migrated to i18n via marketing.privacy_v2.*
 * keys. Other legal pages (Terms, Security, COPPA, GDPR) remain English-only
 * pending counsel review — they're legal language and translation requires
 * a separate locale-by-locale review pass.
 */
import LegalPage from "../../components/LegalPage";
import { useI18n } from "../../lib/i18n";

// ---------- /privacy ----------
export function Privacy() {
  const { t } = useI18n();
  return (
    <LegalPage
      testid="legal-privacy"
      title={t("marketing.privacy_v2.title")}
      metaTitle={t("marketing.privacy_v2.meta_title")}
      metaDescription={t("marketing.privacy_v2.meta_description")}
      summary={t("marketing.privacy_v2.summary")}
    >
      <h2>{t("marketing.privacy_v2.h2_collect")}</h2>
      <p>{t("marketing.privacy_v2.p_collect")}</p>

      <h2>{t("marketing.privacy_v2.h2_never_collect")}</h2>
      <p>{t("marketing.privacy_v2.p_never_collect")}</p>

      <h2>{t("marketing.privacy_v2.h2_use")}</h2>
      <p>{t("marketing.privacy_v2.p_use")}</p>

      <h2>{t("marketing.privacy_v2.h2_rights")}</h2>
      <p>{t("marketing.privacy_v2.p_rights")}</p>

      <h2>{t("marketing.privacy_v2.h2_where")}</h2>
      <p>{t("marketing.privacy_v2.p_where")}</p>

      <h2>{t("marketing.privacy_v2.h2_compliance")}</h2>
      <p>{t("marketing.privacy_v2.p_compliance")}</p>
    </LegalPage>
  );
}

// ---------- /terms ----------
export function Terms() {
  return (
    <LegalPage
      testid="legal-terms"
      title="Terms of service"
      metaTitle="Terms of service — Alouvya"
      metaDescription="The terms you agree to when you use Alouvya."
      summary="Use Alouvya for the calm purpose it was built for. We provide the platform as agreed. You own your data. Either party may end the relationship on reasonable notice."
    >
      <h2>Your account</h2>
      <p>
        Centers sign an order form that governs subscription tier and term.
        Individual parent and staff accounts are provisioned by the center.
        Keep your credentials confidential — every action under your account
        is attributed to you.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Use Alouvya in the spirit of childcare communication. Don't attempt to
        circumvent permissions, scrape data, or interfere with the platform's
        operation. Don't upload content that infringes intellectual property
        or violates applicable law.
      </p>

      <h2>Service availability</h2>
      <p>
        We aim for high availability and publish status updates when planned
        maintenance is needed. We don't promise uninterrupted access; we do
        commit to addressing incidents promptly and communicating openly.
      </p>

      <h2>Your data</h2>
      <p>
        You own the data you put in. We hold it for you. We process it only
        as needed to provide the service. We never sell it.
      </p>

      <h2>Ending the relationship</h2>
      <p>
        Either party may end the subscription with reasonable notice as set
        out in the order form. On termination, you may export your data.
        After a cooling-off period, we delete it from active systems.
      </p>
    </LegalPage>
  );
}

// ---------- /security ----------
export function Security() {
  return (
    <LegalPage
      testid="legal-security"
      title="Security overview"
      metaTitle="Security overview — Alouvya"
      metaDescription="How Alouvya protects the data of children, families, and staff."
      summary="Encryption in transit and at rest. Per-organization isolation. Logged access on every sensitive action. Independent assessments on a regular cadence."
    >
      <h2>Encryption</h2>
      <p>
        All traffic between clients and Alouvya is protected by TLS 1.2 or
        higher. Data at rest in our databases and object storage is encrypted
        with industry-standard algorithms.
      </p>

      <h2>Isolation</h2>
      <p>
        Each organization's data is logically isolated. Every query against the
        platform is scoped to a single organization, and our application
        rejects queries that omit that scope.
      </p>

      <h2>Access control</h2>
      <p>
        Production access is restricted to a small, named group of engineers
        on hardware-secured workstations. Every administrative action is
        logged to an immutable audit trail.
      </p>

      <h2>Vulnerability reporting</h2>
      <p>
        Found something we should know about? Write to{" "}
        <a
          href="mailto:security@alouvya.com"
          className="text-[#1F3A2A] underline underline-offset-4"
        >
          security@alouvya.com
        </a>
        . We acknowledge responsible disclosures within one business day.
      </p>
    </LegalPage>
  );
}

// ---------- /coppa ----------
export function Coppa() {
  return (
    <LegalPage
      testid="legal-coppa"
      title="COPPA notice"
      metaTitle="COPPA notice — Alouvya"
      metaDescription="How Alouvya supports centers serving children under 13 in the United States."
      summary="Children's information is collected only with the verifiable consent of a parent or legal guardian, and only the minimum needed to operate the center's program."
    >
      <h2>Who this applies to</h2>
      <p>
        This notice covers centers operating in the United States that serve
        children under the age of 13.
      </p>

      <h2>What we collect from a child</h2>
      <p>
        Through our Child Interface, a child may tap a friendly tile (Yard,
        Play, Eat, Rest, In Care) to share what they're doing. We collect that
        tap event, attributed to the staff member who confirms it. We do not
        collect free text from children. We do not collect biometric data.
      </p>

      <h2>Parental rights</h2>
      <p>
        A parent or legal guardian may review the information we hold about
        their child, request changes, and revoke consent at any time. Write
        to{" "}
        <a
          href="mailto:privacy@alouvya.com"
          className="text-[#1F3A2A] underline underline-offset-4"
        >
          privacy@alouvya.com
        </a>{" "}
        to begin a request.
      </p>
    </LegalPage>
  );
}

// ---------- /gdpr ----------
export function Gdpr() {
  return (
    <LegalPage
      testid="legal-gdpr"
      title="EU & UK data rights"
      metaTitle="GDPR rights — Alouvya"
      metaDescription="How to exercise GDPR data rights with Alouvya."
      summary="You may request a copy of every record we hold about you or your child. You may correct or delete that information. We respond on the timelines GDPR requires."
    >
      <h2>Your rights</h2>
      <p>You may request, at any time:</p>
      <ul>
        <li>A copy of the personal data we hold about you (right of access)</li>
        <li>Correction of inaccurate or incomplete data (right to rectification)</li>
        <li>Deletion of your personal data (right to erasure)</li>
        <li>Restriction of processing in certain circumstances</li>
        <li>Portability — a structured export you can take elsewhere</li>
        <li>Objection to processing for specific reasons</li>
      </ul>

      <h2>How to exercise them</h2>
      <p>
        Open <strong>More → Manage my data</strong> in your Alouvya app, or
        write to{" "}
        <a
          href="mailto:privacy@alouvya.com"
          className="text-[#1F3A2A] underline underline-offset-4"
        >
          privacy@alouvya.com
        </a>
        . You'll receive an acknowledgement the same business day and a
        response within one calendar month, in line with GDPR Article 12.
      </p>

      <h2>Roles</h2>
      <p>
        Your center is the data <strong>controller</strong>. Alouvya is the
        data <strong>processor</strong> operating on the center's behalf
        under a Data Processing Addendum.
      </p>

      <h2>Supervisory authority</h2>
      <p>
        You may lodge a complaint with the supervisory authority in your
        country of residence at any time.
      </p>
    </LegalPage>
  );
}
